import { Suspense, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { ExternalLink, Download, AlertCircle, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { pdfFrameSrc } from "@/domains/documents/utils/pdfFrameSrc";
import { lazyWithReload } from "@/utils/lazyWithReload";

export interface PDFRendererProps {
  docId: string;
  blobUrl: string;
  /** Omit to render no open-in-tab affordance. Only do that where another
   * escape hatch exists: a browser that will not render a PDF in an iframe
   * fires no `error`, so the fallback below never replaces the blank frame. */
  onOpenInTab?: () => void;
  onDownload: () => void;
  /** Whether this is a DWG file preview (shows disclaimer) */
  isDwgFile?: boolean;
  /** Open the real vector drawing in the Drawings tool. Only supplied for a
   * DWG whose viewer the user may open — this preview is the flattened PDF
   * rendition, so the escape hatch to the true geometry belongs here. */
  onOpenDrawingViewer?: () => void;
  /** Original filename to display in the browser's PDF viewer toolbar */
  filename?: string;
  /** Override the default preview height when the parent owns the available space. */
  className?: string;
  /**
   * Open the viewer scrolled to this 1-based page. Carried as the `#page=`
   * PDF Open Parameter on the blob URL, which the built-in viewers of Chrome,
   * Edge and Firefox honour; a viewer that ignores it simply opens at page 1.
   * Used by the Brief page to jump to a suggestion's cited page.
   */
  initialPage?: number;
  /** Fires only after the browser accepts the PDF iframe as loaded. */
  onLoad?: () => void;
  /** Dialog fallback with app-owned keyboard scrolling and an independent PDF engine. */
  keyboardScroll?: boolean;
}

const PdfFallbackRenderer = lazyWithReload(() => import("./pdf/PdfFallbackRenderer"));

/**
 * Fallback PDF renderer: the native browser PDF viewer in an iframe.
 *
 * The primary preview is the pdf.js page renderer in `./pdf/` (mounted through
 * `PdfRendererBoundary`). This component is what the boundary swaps in when the
 * pdf.js chunk cannot be loaded or pdf.js cannot parse the file, and what the
 * dialog renders when it only has a blob URL and no `Blob` to hand to pdf.js.
 * Uses the blob URL directly for better performance with large files.
 */
export function PDFRenderer({
  docId,
  blobUrl,
  onOpenInTab,
  onDownload,
  isDwgFile,
  onOpenDrawingViewer,
  filename,
  className,
  initialPage,
  onLoad,
  keyboardScroll = false,
}: PDFRendererProps) {
  const sourceKey = `${docId}:${blobUrl}`;
  const [failedSourceKey, setFailedSourceKey] = useState<string | null>(null);
  const hasError = failedSourceKey === sourceKey;
  const { t } = useLanguage();

  const handleIframeError = () => {
    setFailedSourceKey(sourceKey);
  };

  const nativeViewer = (
    <div className={cn("relative h-[65vh] w-full overflow-hidden bg-gray-100", className)}>
      {/* Error state with fallback options */}
      {hasError && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
          <div className="max-w-md p-4 text-center">
            <AlertCircle className="mx-auto mb-2 h-8 w-8 text-amber-500" />
            <p className="mb-2 text-amber-600">{t.documents.preview.unavailable}</p>
            <p className="mb-4 text-sm text-gray-600">{t.documents.preview.unavailableDesc}</p>
            <div className="flex justify-center gap-2">
              {onOpenInTab && (
                <Button onClick={onOpenInTab} variant="outline" size="sm">
                  <ExternalLink className="mr-1 h-4 w-4" />
                  {t.documents.viewer.openInNewTab}
                </Button>
              )}
              <Button onClick={onDownload} variant="outline" size="sm">
                <Download className="mr-1 h-4 w-4" />
                {t.documents.viewer.download}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Native browser PDF viewer using iframe with blob URL */}
      {!hasError && (
        <iframe
          // The page is part of the key: a fragment-only src change does not
          // navigate the frame, so a second citation into the same PDF remounts it.
          key={`${sourceKey}#${initialPage ?? ""}`}
          src={pdfFrameSrc(blobUrl, filename, initialPage)}
          className="h-full w-full border-0"
          title="PDF Preview"
          onErrorCapture={handleIframeError}
          onLoad={onLoad}
        />
      )}

      {/* Quick action bar at bottom */}
      {!hasError && (
        <div className="absolute bottom-4 left-4 right-4 z-20 flex items-center justify-between">
          {isDwgFile && (
            <div className="rounded-lg bg-gray-900/80 px-3 py-2 text-xs text-gray-300 shadow-lg backdrop-blur-sm">
              {t.documents.preview.dwgDisclaimer}
            </div>
          )}
          <div
            className={`flex gap-2 rounded-lg bg-gray-900/80 p-2 shadow-lg backdrop-blur-sm ${!isDwgFile ? "ml-auto" : ""}`}
          >
            {onOpenDrawingViewer && (
              <Button
                onClick={onOpenDrawingViewer}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20 hover:text-white"
              >
                <Eye className="mr-1 h-4 w-4" />
                {t.documents.bulkActions.viewDrawing}
              </Button>
            )}
            {onOpenInTab && (
              <Button
                onClick={onOpenInTab}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20 hover:text-white"
              >
                <ExternalLink className="mr-1 h-4 w-4" />
                {t.documents.viewer.openInNewTab}
              </Button>
            )}
            <Button
              onClick={onDownload}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20 hover:text-white"
            >
              <Download className="mr-1 h-4 w-4" />
              {t.documents.viewer.download}
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  if (!keyboardScroll) return nativeViewer;
  return (
    <ErrorBoundary key={sourceKey} fallback={nativeViewer}>
      <Suspense
        fallback={
          <div className="flex h-full items-center justify-center" role="status">
            {t.documents.viewer.loading}
          </div>
        }
      >
        <PdfFallbackRenderer
          docId={docId}
          blobUrl={blobUrl}
          onDownload={onDownload}
          onOpenInTab={onOpenInTab}
          isDwgFile={isDwgFile}
          onOpenDrawingViewer={onOpenDrawingViewer}
          className={className}
          initialPage={initialPage}
          onLoad={onLoad}
        />
      </Suspense>
    </ErrorBoundary>
  );
}
